import { Modal, Button, Group, ScrollArea, Divider } from "@mantine/core";
import { useState, useEffect } from "react";
import { type CaseFilters as CaseFiltersType } from "../../services";
import CaseFilters from "./CaseFilters";

interface FilterModalProps {
  opened: boolean;
  onClose: () => void;
  filters: CaseFiltersType;
  onApply: (filters: CaseFiltersType) => void;
  /** Optional list of status names to show in the status filter */
  allowedStatusNames?: string[];
  /** Show extra client-side filters for the pathologist review queue */
  showQueueFilters?: boolean;
  /** Show extra client-side filters for the incoming lab queue */
  showIncomingFilters?: boolean;
  showAdditionalStainFilter?: boolean;
  showRelationshipFilters?: boolean;
  pathologistFilterMode?: "editable" | "assigned-user";
  prefixOptions?: string[];
}

export default function FilterModal({
  opened,
  onClose,
  filters,
  onApply,
  allowedStatusNames,
  showQueueFilters,
  showIncomingFilters,
  showAdditionalStainFilter,
  showRelationshipFilters = false,
  pathologistFilterMode,
  prefixOptions,
}: FilterModalProps) {
  // Initialise to empty so no filters are pre-applied before the user opens
  // the modal.  The useEffect below syncs from the parent when the modal opens.
  const [tempFilters, setTempFilters] = useState<CaseFiltersType>({});

  // Sync temp filters when modal opens with new filters
  useEffect(() => {
    if (opened) {
      setTempFilters(filters);
    }
  }, [opened, filters]);

  const handleApply = () => {
    onApply(tempFilters);
    onClose();
  };

  const handleClearAll = () => {
    setTempFilters({});
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Filter Cases"
      size="xl"
      centered
      closeOnClickOutside={false}
      styles={{
        body: {
          display: "flex",
          flexDirection: "column",
          padding: 0,
          height: "75vh",
        },
      }}
    >
      <ScrollArea p="md" style={{ flex: 1, minHeight: 0 }}>
        <CaseFilters
          filters={tempFilters}
          onFiltersChange={setTempFilters}
          allowedStatusNames={allowedStatusNames}
          showQueueFilters={showQueueFilters}
          showIncomingFilters={showIncomingFilters}
          showAdditionalStainFilter={showAdditionalStainFilter}
          showRelationshipFilters={showRelationshipFilters}
          pathologistFilterMode={pathologistFilterMode}
          prefixOptions={prefixOptions}
        />
      </ScrollArea>

      <Divider />
      <Group justify="space-between" p="md" style={{ flexShrink: 0 }}>
        <Button variant="default" onClick={onClose}>
          Cancel
        </Button>
        <Group gap="sm">
          <Button variant="subtle" color="red" onClick={handleClearAll}>
            Clear All
          </Button>
          <Button onClick={handleApply}>Apply Filters</Button>
        </Group>
      </Group>
    </Modal>
  );
}
